// Mount points for F3's AdminPage and AnalyticsPage (docs/FRONTEND-INTEGRATION.md).
// import.meta.glob resolves to an empty map when the module is absent, so this build
// stays green before F3 delivers and shows an honest "not in this build" state.
import type { Config, Session } from '@bcio-voice/contracts';
import { type ComponentType, Suspense, lazy } from 'react';
import type { ApiClient } from '../lib/api.ts';
import { StatePanel } from '../ui/StatePanel.tsx';

export interface FeaturePageProps {
  api: ApiClient;
  session: Session;
  config: Config | null;
}

type Loader = () => Promise<Record<string, unknown>>;
const adminModules = import.meta.glob('../features/admin/index.tsx') as Record<string, Loader>;
const analyticsModules = import.meta.glob('../features/analytics/index.tsx') as Record<string, Loader>;
const routingModules = import.meta.glob('../features/routing/index.tsx') as Record<string, Loader>;
const voicemailModules = import.meta.glob('../features/voicemail/index.tsx') as Record<string, Loader>;
const flowModules = import.meta.glob('../features/flow/index.tsx') as Record<string, Loader>;
const blocklistModules = import.meta.glob('../features/blocklist/index.tsx') as Record<string, Loader>;

function lazyExport(loaders: Record<string, Loader>, exportName: string): ComponentType<FeaturePageProps> | null {
  const loader = Object.values(loaders)[0];
  if (!loader) return null;
  return lazy(async () => {
    const mod = await loader();
    const component = mod[exportName];
    if (typeof component !== 'function' && typeof component !== 'object') {
      throw new Error(`${exportName} is not exported`);
    }
    return { default: component as ComponentType<FeaturePageProps> };
  });
}

const AdminPage = lazyExport(adminModules, 'AdminPage');
const AnalyticsPage = lazyExport(analyticsModules, 'AnalyticsPage');
const RoutingPage = lazyExport(routingModules, 'RoutingPage');
const VoicemailPage = lazyExport(voicemailModules, 'VoicemailPage');
const FlowPage = lazyExport(flowModules, 'FlowPage');
const BlocklistPage = lazyExport(blocklistModules, 'BlocklistPage');

function Missing({ title }: { title: string }) {
  return (
    <div className="page">
      <h1 className="page-title">{title}</h1>
      <StatePanel variant="unavailable" title={`${title} isn't in this build yet`} body="This section is being built separately and hasn't been delivered to this build." />
    </div>
  );
}

function Loading({ label }: { label: string }) {
  return <StatePanel variant="loading" title={`Loading ${label.toLowerCase()}…`} />;
}

export function AdminMount(props: FeaturePageProps) {
  if (!AdminPage) return <Missing title="Admin" />;
  return <Suspense fallback={<Loading label="Admin" />}><AdminPage {...props} /></Suspense>;
}

export function AnalyticsMount(props: FeaturePageProps) {
  if (!AnalyticsPage) return <Missing title="Analytics" />;
  return <Suspense fallback={<Loading label="Analytics" />}><AnalyticsPage {...props} /></Suspense>;
}

export function RoutingMount(props: FeaturePageProps) {
  if (!RoutingPage) return <Missing title="Routing" />;
  return <Suspense fallback={<Loading label="Routing" />}><RoutingPage {...props} /></Suspense>;
}

export function VoicemailMount(props: FeaturePageProps) {
  if (!VoicemailPage) return <Missing title="Voicemail" />;
  return <Suspense fallback={<Loading label="Voicemail" />}><VoicemailPage {...props} /></Suspense>;
}

export function FlowMount(props: FeaturePageProps) {
  if (!FlowPage) return <Missing title="Phone menus" />;
  return <Suspense fallback={<Loading label="Phone menus" />}><FlowPage {...props} /></Suspense>;
}

export function BlocklistMount(props: FeaturePageProps) {
  if (!BlocklistPage) return <Missing title="Blocked and do not call" />;
  return <Suspense fallback={<Loading label="Blocked numbers" />}><BlocklistPage {...props} /></Suspense>;
}
